import React from "react";
import { Button, Spinner } from "react-bootstrap";
import usePatientClinicalMemory from "../../hooks/usePatientClinicalMemory";
import StaleBanner from "./StaleBanner";
import AppointmentStatusCounts from "./AppointmentStatusCounts";

/**
 * ClinicalHistoryCompactPanel: body-only compact history view.
 *
 * Renders WITHOUT a Modal wrapper so it can be:
 *   1. Embedded inline inside another modal (e.g. EditAppointments.jsx) so the
 *      user doesn't see a stacked second dialog.
 *   2. Wrapped by PatientClinicalHistoryCompact.jsx (the standalone modal flavour).
 *
 * Props:
 *   - active (bool): when false, the data hook stays idle (no fetch).
 *   - patientId, practiceId, patientName: identifiers + display name.
 *   - onBack (fn?): if provided, renders a Back button at top-left.
 *   - onShowFull (fn?): if provided, renders a "View Full History" button.
 *   - onClose (fn?): if provided, renders a Close button on the right.
 *   - showHeader (bool): include the title row (default true).
 */
export default function ClinicalHistoryCompactPanel({
  active = true,
  patientId,
  practiceId,
  patientName,
  onBack,
  onShowFull,
  onClose,
  showHeader = true,
}) {
  const { memory, loading, error, isStale, pollingGaveUp, progress, queueState, refresh, checkAgain } =
    usePatientClinicalMemory(active ? patientId : null, practiceId);

  const status = memory?.indexStatus;
  const compact = memory?.compactHistory || {};

  return (
    <div>
      {showHeader && (
        <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
          {/* Left: Back (or empty spacer to keep title centred) */}
          <div style={{ flex: "1 1 0", minWidth: 0 }}>
            {onBack && (
              <Button variant="outline-secondary" size="sm" onClick={onBack} title="Back">
                <i className="fa-solid fa-arrow-left me-1" /> Back
              </Button>
            )}
          </div>
          {/* Centre: title */}
          <h5 className="mb-0 text-center" style={{ flex: "0 1 auto" }}>
            <i className="fa-solid fa-clock-rotate-left me-2 text-primary" />
            History{patientName ? ":" : ""}
            {patientName ? (
              <span className="text-muted small ms-1 fw-normal">{patientName}</span>
            ) : null}
          </h5>
          {/* Right: spacer matching left so title stays centred */}
          <div style={{ flex: "1 1 0" }} />
        </div>
      )}

      {(isStale || status === "INDEXING") && (
        <StaleBanner
          status={status}
          gaveUp={pollingGaveUp}
          progress={progress}
          queueState={queueState}
          onRetry={checkAgain}
        />
      )}

      {error && (
        <div className="alert alert-danger py-2 px-3 small mb-3">
          <i className="fa-solid fa-triangle-exclamation me-2" />
          {error}
        </div>
      )}

      {loading && !memory ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      ) : status === "NOT_INDEXED" ? (
        <div className="text-center py-3">
          <p className="text-muted mb-3">
            No clinical history has been built yet for this patient.
          </p>
          <Button variant="primary" size="sm" onClick={refresh}>
            <i className="fa-solid fa-rotate me-2" /> Build History
          </Button>
        </div>
      ) : status === "FAILED" ? (
        <div className="alert alert-danger py-2 px-3 small mb-0">
          <i className="fa-solid fa-circle-xmark me-2" />
          Last build failed{memory?.lastError?.message ? `: ${memory.lastError.message}` : ""}.
          Press Refresh to try again.
        </div>
      ) : (
        <>
          <AppointmentStatusCounts patientId={patientId} practiceId={practiceId} />

          <section className="mb-3">
            <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">Summary</h6>
            <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
              {compact.historySummaryParagraph || "No summary available yet."}
            </p>
          </section>

          <section>
            <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">
              Suggested Focus for Today
            </h6>
            <div className="alert alert-primary py-2 px-3 small mb-2 d-flex align-items-center">
              <i className="fa-light fa-sparkles text-primary me-2" />
              AI-suggested. Practitioner must review.
            </div>
            {compact.suggestedFocusForToday?.length ? (
              <ul className="ps-3 mb-0">
                {compact.suggestedFocusForToday.map((it, i) => (
                  <li key={i} className="mb-1">{it}</li>
                ))}
              </ul>
            ) : (
              <div className="text-muted small fst-italic">No suggested focus yet.</div>
            )}
          </section>
        </>
      )}

      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={refresh}
          disabled={loading || status === "INDEXING"}
        >
          <i className="fa-solid fa-rotate me-1" /> Refresh
        </Button>
        {onShowFull && (
          <Button variant="primary" size="sm" onClick={onShowFull}>
            View Full History
          </Button>
        )}
        {onClose && (
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        )}
      </div>
    </div>
  );
}
